/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React, {useState, useEffect, useRef, useCallback} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'

// Project Components
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'

// Components
import {
    Box,
    Divider,
    Drawer,
    DrawerBody,
    DrawerOverlay,
    DrawerContent,
    // Hooks
    useBreakpointValue,
    useMultiStyleConfig,
    List,
    Link
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {
    AuthHelpers,
    useAuthHelper,
    useCustomerType,
    useAccessToken
} from '@salesforce/commerce-sdk-react'
// Icons
// import {
//     BrandLogo,
//     SignoutIcon,
//     StoreIcon,
//     UserIcon
// } from '@salesforce/retail-react-app/app/components/icons'

// Others
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
//import {getPathWithLocale, categoryUrlBuilder} from '@salesforce/retail-react-app/app/utils/url'
import LoadingSpinner from '@salesforce/retail-react-app/app/components/loading-spinner'

import useNavigation from '@salesforce/retail-react-app/app/hooks/use-navigation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {AuthModal, useAuthModal} from '../../hooks/use-auth-modal'

import {ListMenuContent} from '../list-menu/list-menu-content'
import {getControllerUrl} from '../../utills/controllerUrlGenerator'
import sites from '../../../../config/sites'

import {ListMenuContentInner} from '../list-menu/list-menu-content-inner'
import {MOBILE_NAV_INTIAL_DEATURE_IMAGE, MEGA_MENU_CONTENT_SLOT_ID} from '../../constants'
import {Link as RouteLink} from 'react-router-dom'
import {TrackOrderModal, useTrackOrderModal} from '../../hooks/use-track-order-modal'
import useCustomFetch from '../../hooks/useFetchContent'
import DrawerLoginBox from './drawer-login-box'
import {pxToRem} from '../../utills/pxToRem'
import {fonts} from '../../theme/foundations/fonts'
// The FONT_SIZES and FONT_WEIGHTS constants are used to control the styling for
// the accordion buttons as their current depth. In the below definition we assign
// values for depths 0 - 3, any depth deeper than that will use the default styling.
const PHONE_DRAWER_SIZE = 'xs'
const TABLET_DRAWER_SIZE = 'lg'

const getContentInfo = (html) => {
    if (!html) return {hasContent: false, contentCount: 0}
    const stripped = html.replace(/<!--[\s\S]*?-->/g, '').trim()
    if (!stripped) return {hasContent: false, contentCount: 0}
    const liMatches = stripped.match(/<li\b[^>]*>/gi)
    const count = liMatches ? liMatches.length : 0
    return {hasContent: count > 0, contentCount: count}
}

const mobileContentSlotStyles = {
    '.shop-by-brand-carousel-wrapper': {
        position: 'relative',
        width: '100%',
        maxWidth: '100%'
    },
    '.shop-by-brand-carousel-arrow': {
        display: 'none'
    },
    '.shop-by-brand-carousel': {
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'nowrap',
        gap: pxToRem(10),
        p: 0,
        m: 0,
        overflow: 'hidden',
        minWidth: 0,
        width: '100%',
        listStyle: 'none',
        li: {
            listStyle: 'none',
            textAlign: 'center',
            flex: '0 0 auto',
            boxSizing: 'border-box',
            img: {
                maxWidth: '100%',
                height: 'auto',
                display: 'block'
            },
            '.shop-by-brand-image-wrapper-style1': {
                position: 'relative',
                width: pxToRem(182),
                height: pxToRem(182),
                display: 'inline-block',
                overflow: 'hidden'
            },
            '.shop-by-brand-main-image-style1': {
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'cover'
            },
            '.shop-by-brand-overlay-image-style1': {
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                maxWidth: '55%',
                maxHeight: '55%',
                objectFit: 'contain'
            },
            '.shop-by-brand-sale-button-container-style1': {
                fontSize: pxToRem(12),
                m: 0,
                mt: pxToRem(10),
                fontWeight: 700,
                letterSpacing: pxToRem(1.5),
                fontFamily: fonts.primary,
                textTransform: 'uppercase',
                '.shop-by-brand-sale-button-style1': {
                    color: 'black',
                    _hover: {
                        color: 'cav_red',
                        textDecoration: 'none'
                    }
                }
            }
        }
    }
}

const ARROW_SVG = (id, size = 30) =>
    `<svg width="${size}" height="${size}" viewBox="0 0 40 40"><use href="#${id}" /></svg>`

/**
 * This is the navigation component used for mobile devices (phone and tablet). It's
 * main usage is to navigate from one category to the next, but also homes links to
 * support, log in and out actions, as support links.
 */
const DrawerMenu = ({
    root,
    itemsKey,
    itemComponent,
    isOpen,
    onClose = noop,
    menuToggle,
    setMenuToggle,
    isStickyActive,
    promoBannerInHeaderIsActive
}) => {
    const intl = useIntl()
    const {isRegistered} = useCustomerType()
    const styles = useMultiStyleConfig('DrawerMenu')
    const navigate = useNavigation()
    const authModal = useAuthModal()
    const [drawerEmail, setDrawerEmail] = useState('')
    const trackOrderModal = useTrackOrderModal()
    const drawerSize = useBreakpointValue({sm: PHONE_DRAWER_SIZE, md: TABLET_DRAWER_SIZE})
    const socialIconVariant = useBreakpointValue({base: 'flex', md: 'flex-start'})
    const {site, buildUrl} = useMultiSite()
    const {l10n} = site
    const [showLoading, setShowLoading] = useState(false)
    const [ariaBusy, setAriaBusy] = useState('true')
    const logout = useAuthHelper(AuthHelpers.Logout)
    const drawerBodyRef = useRef(null)
    const onSignoutClick = async () => {
        await logout.mutateAsync()
        setMenuToggle(!menuToggle)
        // navigate('/login')
        window.location.href = '/login/'
    }
    const MainContentComponent = itemComponent

    const mobileFeatureImage = `<img src=${getAssetUrl(
        MOBILE_NAV_INTIAL_DEATURE_IMAGE
    )} loading="lazy" alt="cavender home" title="cavender home">`

    const [featureImage, setfeatureImage] = useState(mobileFeatureImage)
    const [featureImageMobileLevelTwo, setFeatureImageMobileLevelTwo] = useState('')
    const [featureImageMobileLevelThree, setFeatureImageMobileLevelThree] = useState('')
    const [featureImageMobileLevelFour, setFeatureImageMobileLevelFour] = useState('')
    const [selectedLevelOneMenu, setSelectedLevelOneMenu] = useState({})
    const [linksForLevelTwoMenu, setLinksForLevelTwoMenu] = useState([])
    const [selectedLevelTwoMenu, setSelectedLevelTwoMenu] = useState({})
    const [linksForLevelThreeMenu, setLinksForLevelThreeMenu] = useState([])
    const [isLevelTwoMenuOpen, setIsLevelTwoMenuOpen] = useState(false)
    const [isLevelThreeMenuOpen, setIsLevelThreeMenuOpen] = useState(false)
    const [linksForContentComp, setLinksForContentComp] = useState({})

    // Content slot
    const {token} = useAccessToken()
    const mobileContentSlotRef = useRef(null)
    const currentCategoryForSlot =
        Object.keys(linksForContentComp)?.length > 0
            ? linksForContentComp.name?.toLowerCase()
            : null
    const contentSlotId = currentCategoryForSlot
        ? `${MEGA_MENU_CONTENT_SLOT_ID}${encodeURIComponent(currentCategoryForSlot)}`
        : null
    const {response: contentSlotResponse, loading: contentSlotLoading} = useCustomFetch(
        token,
        contentSlotId
    )
    const {hasContent: hasSlotContent, contentCount: slotContentCount} =
        getContentInfo(contentSlotResponse)

    // Global content slot (level 1)
    const [globalContentSlotEl, setGlobalContentSlotEl] = useState(null)
    const globalContentSlotRef = useCallback((node) => {
        setGlobalContentSlotEl(node)
    }, [])
    const {response: globalSlotResponse, loading: globalSlotLoading} = useCustomFetch(
        token,
        'megamenu-slot-global'
    )
    const {hasContent: hasGlobalSlotContent, contentCount: globalSlotContentCount} =
        getContentInfo(globalSlotResponse)

    const supportedLocaleIds = l10n?.supportedLocales.map((locale) => locale.id)
    const showLocaleSelector = supportedLocaleIds?.length > 1

    const handleClose = () => {
        // Reset navigation state to L1 so reopening starts fresh
        setLinksForContentComp({})
        setLinksForLevelTwoMenu([])
        setLinksForLevelThreeMenu([])
        setIsLevelTwoMenuOpen(false)
        setIsLevelThreeMenuOpen(false)
        setSelectedLevelOneMenu({})
        setSelectedLevelTwoMenu({})
        setfeatureImage(mobileFeatureImage)
        setFeatureImageMobileLevelTwo('')
        setFeatureImageMobileLevelThree('')
        setFeatureImageMobileLevelFour('')
        onClose()
        setMenuToggle(!menuToggle)
    }

    useEffect(() => {
        setAriaBusy('false')
    }, [])

    // Reset scroll position to top only when drawer transitions from closed to open (not on re-renders or keyboard).
    const prevIsOpenRef = useRef(false)
    useEffect(() => {
        const wasOpen = prevIsOpenRef.current
        prevIsOpenRef.current = isOpen
        const justOpened = isOpen && !wasOpen
        if (!justOpened) return
        const body = drawerBodyRef.current
        if (body) body.scrollTop = 0
    }, [isOpen])

    // Reset scroll position when navigating between menu levels
    useEffect(() => {
        if (drawerBodyRef.current) {
            drawerBodyRef.current.scrollTop = 0
        }
    }, [linksForContentComp, linksForLevelTwoMenu, linksForLevelThreeMenu])

    // Dynamically calculate header height for drawer positioning
    const [drawerTop, setDrawerTop] = useState(0)
    // When keyboard is open, drawer height = visible viewport height (menu moves up by exactly keyboard height)
    const [drawerHeight, setDrawerHeight] = useState(0)
    const [stableViewportHeight, setStableViewportHeight] = useState(0)
    const lastViewportWidthRef = useRef(0)
    const lastStableHeightRef = useRef(0)
    const updateTopRef = useRef(null)
    useEffect(() => {
        if (!isOpen) return
        const initialHeight = window.innerHeight
        setStableViewportHeight(initialHeight)
        lastStableHeightRef.current = initialHeight
        lastViewportWidthRef.current = window.innerWidth
        const updateLayout = () => {
            const headerEl = document.getElementById('sticky-header')
            if (!headerEl) return
            const rect = headerEl.getBoundingClientRect()
            const headerBottom = rect.bottom
            const stable = lastStableHeightRef.current || window.innerHeight
            const vv = typeof window !== 'undefined' && window.visualViewport
            const keyboardOpen = vv && vv.height < window.innerHeight * 0.9
            if (keyboardOpen) {
                setDrawerTop(vv.offsetTop)
                setDrawerHeight(vv.height)
                requestAnimationFrame(() => {
                    const active = document.activeElement
                    const body = drawerBodyRef.current
                    if (active && body && body.contains(active)) {
                        const inputRect = active.getBoundingClientRect()
                        const padding = 24
                        const targetTop = vv.offsetTop + vv.height - inputRect.height - padding
                        const delta = targetTop - inputRect.top
                        if (Math.abs(delta) > 5) {
                            const newScrollTop = body.scrollTop - delta
                            body.scrollTop = Math.max(
                                0,
                                Math.min(newScrollTop, body.scrollHeight - body.clientHeight)
                            )
                        }
                    }
                })
            } else {
                setDrawerTop(headerBottom)
                setDrawerHeight(stable - headerBottom)
            }
        }
        updateTopRef.current = updateLayout
        const handleResize = () => {
            if (window.innerWidth !== lastViewportWidthRef.current) {
                lastViewportWidthRef.current = window.innerWidth
                const h = window.innerHeight
                setStableViewportHeight(h)
                lastStableHeightRef.current = h
            }
            updateLayout()
        }
        const scheduleUpdateLayout = () => {
            requestAnimationFrame(updateLayout)
        }
        updateLayout()
        window.addEventListener('scroll', updateLayout)
        window.addEventListener('resize', handleResize)
        const vv = typeof window !== 'undefined' && window.visualViewport
        if (vv) {
            vv.addEventListener('resize', scheduleUpdateLayout)
            vv.addEventListener('scroll', scheduleUpdateLayout)
        }
        return () => {
            window.removeEventListener('scroll', updateLayout)
            window.removeEventListener('resize', handleResize)
            if (vv) {
                vv.removeEventListener('resize', scheduleUpdateLayout)
                vv.removeEventListener('scroll', scheduleUpdateLayout)
            }
            updateTopRef.current = null
        }
    }, [isOpen, isStickyActive, promoBannerInHeaderIsActive])

    // When keyboard is open (drawer resized to visible viewport), scroll menu to bottom so input/button are visible.
    // Defer until after layout so we don't get overwritten by Drawer re-render or browser layout.
    useEffect(() => {
        if (!isOpen) return
        const vv = typeof window !== 'undefined' && window.visualViewport
        if (!vv || vv.height >= window.innerHeight * 0.9) return
        const scrollToBottom = () => {
            const body = drawerBodyRef.current
            if (body) {
                body.scrollTop = body.scrollHeight - body.clientHeight
            }
        }
        const id = requestAnimationFrame(() => {
            requestAnimationFrame(scrollToBottom)
        })
        const t = setTimeout(scrollToBottom, 150)
        return () => {
            cancelAnimationFrame(id)
            clearTimeout(t)
        }
    }, [drawerTop, drawerHeight, isOpen])

    const handleDrawerInputBlur = useCallback(() => {
        setTimeout(() => {
            updateTopRef.current?.()
        }, 100)
    }, [])

    // Content slot carousel
    useEffect(() => {
        if (!hasSlotContent || !mobileContentSlotRef.current) return
        const el = mobileContentSlotRef.current
        const outerContainer = el.parentElement

        const allImages = el.querySelectorAll('img')
        allImages.forEach((img) => {
            img.removeAttribute('width')
            img.removeAttribute('height')
        })

        const carousel = el.querySelector('.shop-by-brand-carousel')
        if (!carousel || !outerContainer) return

        const liItems = carousel.querySelectorAll(':scope > li')
        if (liItems.length <= 1) {
            carousel.style.justifyContent = 'flex-start'
            el.style.paddingRight = '0'
            return
        }

        carousel.style.justifyContent = 'flex-start'
        outerContainer.style.transition = 'padding-left 0.3s ease'
        let rightArrowBtn = null
        let leftArrowBtn = null

        // Drag scrolling (mouse + touch)
        let isDragging = false
        let dragStartX = 0
        let dragScrollLeft = 0

        const onDragStart = (x) => {
            isDragging = true
            carousel.style.cursor = 'grabbing'
            dragStartX = x
            dragScrollLeft = carousel.scrollLeft
        }
        const onDragEnd = () => {
            isDragging = false
            carousel.style.cursor = 'grab'
        }
        const onDragMove = (x, e) => {
            if (!isDragging) return
            if (e) e.preventDefault()
            carousel.scrollLeft = dragScrollLeft - (x - dragStartX)
        }

        const onMouseDown = (e) => onDragStart(e.pageX)
        const onMouseLeave = () => {
            if (isDragging) onDragEnd()
        }
        const onMouseUp = () => onDragEnd()
        const onMouseMove = (e) => onDragMove(e.pageX, e)

        const onTouchStart = (e) => onDragStart(e.touches[0].pageX)
        const onTouchEnd = () => onDragEnd()
        const onTouchMove = (e) => onDragMove(e.touches[0].pageX, e)

        const checkOverflow = () => {
            const gap = parseFloat(getComputedStyle(carousel).columnGap) || 10
            let totalWidth = 0
            const currentLiItems = carousel.querySelectorAll(':scope > li')
            currentLiItems.forEach((item, i) => {
                totalWidth += item.getBoundingClientRect().width
                if (i < currentLiItems.length - 1) totalWidth += gap
            })
            return totalWidth > carousel.getBoundingClientRect().width
        }

        const getScrollAmount = () => {
            const firstItem = carousel.querySelector(':scope > li')
            if (!firstItem) return 200
            const gap = parseFloat(getComputedStyle(carousel).columnGap) || 10
            return firstItem.getBoundingClientRect().width + gap
        }

        const handleRightClick = () => {
            carousel.scrollBy({left: getScrollAmount(), behavior: 'smooth'})
        }

        const handleLeftClick = () => {
            carousel.scrollBy({left: -getScrollAmount(), behavior: 'smooth'})
        }

        const updateArrows = () => {
            const maxScroll = carousel.scrollWidth - carousel.clientWidth
            const atEnd = carousel.scrollLeft >= maxScroll - 1
            if (leftArrowBtn) {
                leftArrowBtn.style.display = carousel.scrollLeft > 1 ? 'flex' : 'none'
            }
            if (rightArrowBtn) {
                rightArrowBtn.style.display = !atEnd ? 'flex' : 'none'
            }
            outerContainer.style.paddingLeft = atEnd ? '0' : ''
        }

        const arrowBaseStyle = `
            align-items: center;
            justify-content: center;
            position: absolute;
            transform: translateY(-50%);
            background: none;
            border: none;
            width: ${pxToRem(30)};
            height: ${pxToRem(30)};
            cursor: pointer;
            padding: 0;
            z-index: 10;
        `

        const createArrows = () => {
            outerContainer.style.position = 'relative'
            const arrowTop = el.offsetTop + el.offsetHeight / 2

            rightArrowBtn = document.createElement('button')
            rightArrowBtn.setAttribute('aria-label', 'Next')
            rightArrowBtn.innerHTML = ARROW_SVG('right-arrow')
            rightArrowBtn.style.cssText = `display: flex; right: ${pxToRem(
                5
            )}; top: ${arrowTop}px; ${arrowBaseStyle}`
            rightArrowBtn.addEventListener('mouseenter', () => {
                rightArrowBtn.innerHTML = ARROW_SVG('right-arrow-hover')
            })
            rightArrowBtn.addEventListener('mouseleave', () => {
                rightArrowBtn.innerHTML = ARROW_SVG('right-arrow')
            })
            rightArrowBtn.addEventListener('click', handleRightClick)
            outerContainer.appendChild(rightArrowBtn)

            leftArrowBtn = document.createElement('button')
            leftArrowBtn.setAttribute('aria-label', 'Previous')
            leftArrowBtn.innerHTML = ARROW_SVG('right-arrow')
            leftArrowBtn.style.cssText = `display: none; left: ${pxToRem(
                5
            )}; top: ${arrowTop}px; ${arrowBaseStyle} transform: scaleX(-1) translateY(-50%);`
            leftArrowBtn.addEventListener('mouseenter', () => {
                leftArrowBtn.innerHTML = ARROW_SVG('right-arrow-hover')
            })
            leftArrowBtn.addEventListener('mouseleave', () => {
                leftArrowBtn.innerHTML = ARROW_SVG('right-arrow')
            })
            leftArrowBtn.addEventListener('click', handleLeftClick)
            outerContainer.appendChild(leftArrowBtn)

            carousel.addEventListener('scroll', updateArrows)
            carousel.style.cursor = 'grab'
            carousel.addEventListener('mousedown', onMouseDown)
            carousel.addEventListener('mouseleave', onMouseLeave)
            carousel.addEventListener('mouseup', onMouseUp)
            carousel.addEventListener('mousemove', onMouseMove)
            carousel.addEventListener('touchstart', onTouchStart, {passive: false})
            carousel.addEventListener('touchend', onTouchEnd)
            carousel.addEventListener('touchmove', onTouchMove, {passive: false})
            updateArrows()
        }

        const removeArrows = () => {
            carousel.removeEventListener('scroll', updateArrows)
            carousel.removeEventListener('mousedown', onMouseDown)
            carousel.removeEventListener('mouseleave', onMouseLeave)
            carousel.removeEventListener('mouseup', onMouseUp)
            carousel.removeEventListener('mousemove', onMouseMove)
            carousel.removeEventListener('touchstart', onTouchStart)
            carousel.removeEventListener('touchend', onTouchEnd)
            carousel.removeEventListener('touchmove', onTouchMove)
            carousel.style.cursor = ''
            if (rightArrowBtn) {
                rightArrowBtn.removeEventListener('click', handleRightClick)
                rightArrowBtn.remove()
                rightArrowBtn = null
            }
            if (leftArrowBtn) {
                leftArrowBtn.removeEventListener('click', handleLeftClick)
                leftArrowBtn.remove()
                leftArrowBtn = null
            }
            outerContainer.style.position = ''
            outerContainer.style.paddingLeft = ''
        }

        const expandItems = () => {
            const items = carousel.querySelectorAll(':scope > li')
            items.forEach((li) => {
                li.style.flex = '1 1 0%'
                const wrapper = li.querySelector('.shop-by-brand-image-wrapper-style1')
                if (wrapper) {
                    wrapper.style.width = '100%'
                    wrapper.style.height = 'auto'
                    wrapper.style.aspectRatio = '1 / 1'
                }
            })
        }

        const resetItems = () => {
            const items = carousel.querySelectorAll(':scope > li')
            items.forEach((li) => {
                li.style.flex = '0 0 auto'
                const wrapper = li.querySelector('.shop-by-brand-image-wrapper-style1')
                if (wrapper) {
                    wrapper.style.width = ''
                    wrapper.style.height = ''
                    wrapper.style.aspectRatio = ''
                }
            })
            carousel.style.paddingRight = ''
        }

        const applyLayout = () => {
            resetItems()
            if (checkOverflow()) {
                el.style.paddingRight = '0'
                carousel.style.paddingRight = pxToRem(10)
                if (!rightArrowBtn) createArrows()
                const arrowTop = el.offsetTop + el.offsetHeight / 2
                if (rightArrowBtn) rightArrowBtn.style.top = arrowTop + 'px'
                if (leftArrowBtn) leftArrowBtn.style.top = arrowTop + 'px'
                updateArrows()
            } else {
                el.style.paddingRight = pxToRem(10)
                removeArrows()
                expandItems()
            }
        }

        applyLayout()
        window.addEventListener('resize', applyLayout)
        return () => {
            window.removeEventListener('resize', applyLayout)
            removeArrows()
        }
    }, [contentSlotResponse, hasSlotContent])

    // Global content slot carousel
    useEffect(() => {
        if (!hasGlobalSlotContent || !globalContentSlotEl) return
        const el = globalContentSlotEl
        const outerContainer = el.parentElement

        const allImages = el.querySelectorAll('img')
        allImages.forEach((img) => {
            img.removeAttribute('width')
            img.removeAttribute('height')
        })

        const carousel = el.querySelector('.shop-by-brand-carousel')
        if (!carousel || !outerContainer) return

        const liItems = carousel.querySelectorAll(':scope > li')
        if (liItems.length <= 1) {
            carousel.style.justifyContent = 'flex-start'
            el.style.paddingRight = '0'
            return
        }

        carousel.style.justifyContent = 'flex-start'
        outerContainer.style.transition = 'padding-left 0.3s ease'
        let rightArrowBtn = null
        let leftArrowBtn = null

        // Drag scrolling (mouse + touch)
        let isDragging = false
        let dragStartX = 0
        let dragScrollLeft = 0

        const onDragStart = (x) => {
            isDragging = true
            carousel.style.cursor = 'grabbing'
            dragStartX = x
            dragScrollLeft = carousel.scrollLeft
        }
        const onDragEnd = () => {
            isDragging = false
            carousel.style.cursor = 'grab'
        }
        const onDragMove = (x, e) => {
            if (!isDragging) return
            if (e) e.preventDefault()
            carousel.scrollLeft = dragScrollLeft - (x - dragStartX)
        }

        const onMouseDown = (e) => onDragStart(e.pageX)
        const onMouseLeave = () => {
            if (isDragging) onDragEnd()
        }
        const onMouseUp = () => onDragEnd()
        const onMouseMove = (e) => onDragMove(e.pageX, e)

        const onTouchStart = (e) => onDragStart(e.touches[0].pageX)
        const onTouchEnd = () => onDragEnd()
        const onTouchMove = (e) => onDragMove(e.touches[0].pageX, e)

        const checkOverflow = () => {
            const gap = parseFloat(getComputedStyle(carousel).columnGap) || 10
            let totalWidth = 0
            const currentLiItems = carousel.querySelectorAll(':scope > li')
            currentLiItems.forEach((item, i) => {
                totalWidth += item.getBoundingClientRect().width
                if (i < currentLiItems.length - 1) totalWidth += gap
            })
            return totalWidth > carousel.getBoundingClientRect().width
        }

        const getScrollAmount = () => {
            const firstItem = carousel.querySelector(':scope > li')
            if (!firstItem) return 200
            const gap = parseFloat(getComputedStyle(carousel).columnGap) || 10
            return firstItem.getBoundingClientRect().width + gap
        }

        const handleRightClick = () => {
            carousel.scrollBy({left: getScrollAmount(), behavior: 'smooth'})
        }

        const handleLeftClick = () => {
            carousel.scrollBy({left: -getScrollAmount(), behavior: 'smooth'})
        }

        const updateArrows = () => {
            const maxScroll = carousel.scrollWidth - carousel.clientWidth
            const atEnd = carousel.scrollLeft >= maxScroll - 1
            if (leftArrowBtn) {
                leftArrowBtn.style.display = carousel.scrollLeft > 1 ? 'flex' : 'none'
            }
            if (rightArrowBtn) {
                rightArrowBtn.style.display = !atEnd ? 'flex' : 'none'
            }
            outerContainer.style.paddingLeft = atEnd ? '0' : ''
        }

        const arrowBaseStyle = `
            align-items: center;
            justify-content: center;
            position: absolute;
            transform: translateY(-50%);
            background: none;
            border: none;
            width: ${pxToRem(30)};
            height: ${pxToRem(30)};
            cursor: pointer;
            padding: 0;
            z-index: 10;
        `

        const createArrows = () => {
            outerContainer.style.position = 'relative'
            const arrowTop = el.offsetTop + el.offsetHeight / 2

            rightArrowBtn = document.createElement('button')
            rightArrowBtn.setAttribute('aria-label', 'Next')
            rightArrowBtn.innerHTML = ARROW_SVG('right-arrow')
            rightArrowBtn.style.cssText = `display: flex; right: ${pxToRem(
                5
            )}; top: ${arrowTop}px; ${arrowBaseStyle}`
            rightArrowBtn.addEventListener('mouseenter', () => {
                rightArrowBtn.innerHTML = ARROW_SVG('right-arrow-hover')
            })
            rightArrowBtn.addEventListener('mouseleave', () => {
                rightArrowBtn.innerHTML = ARROW_SVG('right-arrow')
            })
            rightArrowBtn.addEventListener('click', handleRightClick)
            outerContainer.appendChild(rightArrowBtn)

            leftArrowBtn = document.createElement('button')
            leftArrowBtn.setAttribute('aria-label', 'Previous')
            leftArrowBtn.innerHTML = ARROW_SVG('right-arrow')
            leftArrowBtn.style.cssText = `display: none; left: ${pxToRem(
                5
            )}; top: ${arrowTop}px; ${arrowBaseStyle} transform: scaleX(-1) translateY(-50%);`
            leftArrowBtn.addEventListener('mouseenter', () => {
                leftArrowBtn.innerHTML = ARROW_SVG('right-arrow-hover')
            })
            leftArrowBtn.addEventListener('mouseleave', () => {
                leftArrowBtn.innerHTML = ARROW_SVG('right-arrow')
            })
            leftArrowBtn.addEventListener('click', handleLeftClick)
            outerContainer.appendChild(leftArrowBtn)

            carousel.addEventListener('scroll', updateArrows)
            carousel.style.cursor = 'grab'
            carousel.addEventListener('mousedown', onMouseDown)
            carousel.addEventListener('mouseleave', onMouseLeave)
            carousel.addEventListener('mouseup', onMouseUp)
            carousel.addEventListener('mousemove', onMouseMove)
            carousel.addEventListener('touchstart', onTouchStart, {passive: false})
            carousel.addEventListener('touchend', onTouchEnd)
            carousel.addEventListener('touchmove', onTouchMove, {passive: false})
            updateArrows()
        }

        const removeArrows = () => {
            carousel.removeEventListener('scroll', updateArrows)
            carousel.removeEventListener('mousedown', onMouseDown)
            carousel.removeEventListener('mouseleave', onMouseLeave)
            carousel.removeEventListener('mouseup', onMouseUp)
            carousel.removeEventListener('mousemove', onMouseMove)
            carousel.removeEventListener('touchstart', onTouchStart)
            carousel.removeEventListener('touchend', onTouchEnd)
            carousel.removeEventListener('touchmove', onTouchMove)
            carousel.style.cursor = ''
            if (rightArrowBtn) {
                rightArrowBtn.removeEventListener('click', handleRightClick)
                rightArrowBtn.remove()
                rightArrowBtn = null
            }
            if (leftArrowBtn) {
                leftArrowBtn.removeEventListener('click', handleLeftClick)
                leftArrowBtn.remove()
                leftArrowBtn = null
            }
            outerContainer.style.position = ''
        }

        const expandItems = () => {
            const items = carousel.querySelectorAll(':scope > li')
            items.forEach((li) => {
                li.style.flex = '1 1 0%'
                const wrapper = li.querySelector('.shop-by-brand-image-wrapper-style1')
                if (wrapper) {
                    wrapper.style.width = '100%'
                    wrapper.style.height = 'auto'
                    wrapper.style.aspectRatio = '1 / 1'
                }
            })
        }

        const resetItems = () => {
            const items = carousel.querySelectorAll(':scope > li')
            items.forEach((li) => {
                li.style.flex = '0 0 auto'
                const wrapper = li.querySelector('.shop-by-brand-image-wrapper-style1')
                if (wrapper) {
                    wrapper.style.width = ''
                    wrapper.style.height = ''
                    wrapper.style.aspectRatio = ''
                }
            })
            carousel.style.paddingRight = ''
        }

        const applyLayout = () => {
            resetItems()
            if (checkOverflow()) {
                el.style.paddingRight = '0'
                carousel.style.paddingRight = pxToRem(10)
                if (!rightArrowBtn) createArrows()
                const arrowTop = el.offsetTop + el.offsetHeight / 2
                if (rightArrowBtn) rightArrowBtn.style.top = arrowTop + 'px'
                if (leftArrowBtn) leftArrowBtn.style.top = arrowTop + 'px'
                updateArrows()
            } else {
                el.style.paddingRight = pxToRem(10)
                removeArrows()
                expandItems()
            }
        }

        // Run layout immediately
        applyLayout()

        // Re-run after images load to get correct positioning
        const imgElements = el.querySelectorAll('img')
        const onImgLoad = () => applyLayout()
        imgElements.forEach((img) => {
            if (!img.complete) {
                img.addEventListener('load', onImgLoad)
            }
        })

        window.addEventListener('resize', applyLayout)
        return () => {
            window.removeEventListener('resize', applyLayout)
            imgElements.forEach((img) => {
                img.removeEventListener('load', onImgLoad)
            })
            removeArrows()
        }
    }, [globalSlotResponse, hasGlobalSlotContent, globalContentSlotEl])

    const handleOnAccountClick = () => {
        handleClose()
        // setMenuToggle((toggle) => !toggle)
        authModal.onOpen()
    }

    const handleTrackOrderClick = (e) => {
        e?.preventDefault()
        e?.stopPropagation()
        if (isRegistered) {
            const path = buildUrl('/account/orders')
            navigate(path)
        } else {
            handleClose()
            trackOrderModal.onOpen()
        }
    }

    return (
        <>
            <Drawer
                isOpen={isOpen}
                onClose={handleClose}
                placement="left"
                size={drawerSize}
                isFullHeight={false}
            >
                <DrawerOverlay {...styles.drawerOverlay}>
                    <DrawerContent
                        width="100% !important"
                        maxWidth="100%"
                        top={`${drawerTop}px !important`}
                        height={
                            drawerHeight > 0
                                ? `${drawerHeight}px`
                                : stableViewportHeight > 0
                                ? `${stableViewportHeight - drawerTop}px`
                                : `calc(100dvh - ${drawerTop}px)`
                        }
                        borderRadius={0}
                        overflow="hidden"
                        borderBottom={`${pxToRem(5)} solid white`}
                    >
                        {/* Main Content */}
                        <DrawerBody ref={drawerBodyRef} {...styles.drawerBody}>
                            <div
                                id="category-nav"
                                role="group"
                                aria-live="polite"
                                aria-busy={ariaBusy}
                                aria-atomic="true"
                                style={{paddingRight: pxToRem(11), paddingLeft: pxToRem(11)}}
                                onKeyDown={(e) => {
                                    if (
                                        e.key === 'ArrowDown' ||
                                        e.key === 'ArrowUp' ||
                                        e.key === 'ArrowLeft' ||
                                        e.key === 'ArrowRight'
                                    ) {
                                        e.preventDefault()
                                        const focusableElements = e.currentTarget.querySelectorAll(
                                            'a[href]:not([disabled]), button:not([disabled])'
                                        )
                                        if (!focusableElements || focusableElements.length === 0)
                                            return

                                        const currentIndex = Array.from(focusableElements).indexOf(
                                            document.activeElement
                                        )

                                        let nextIndex = currentIndex

                                        if (e.key === 'ArrowDown') {
                                            nextIndex =
                                                currentIndex === -1 ||
                                                currentIndex === focusableElements.length - 1
                                                    ? 0
                                                    : currentIndex + 1
                                        } else if (e.key === 'ArrowUp') {
                                            nextIndex =
                                                currentIndex === -1 || currentIndex === 0
                                                    ? focusableElements.length - 1
                                                    : currentIndex - 1
                                        } else if (e.key === 'ArrowRight') {
                                            nextIndex =
                                                currentIndex === -1 ||
                                                currentIndex === focusableElements.length - 1
                                                    ? 0
                                                    : currentIndex + 1
                                        } else if (e.key === 'ArrowLeft') {
                                            nextIndex =
                                                currentIndex === -1 || currentIndex === 0
                                                    ? focusableElements.length - 1
                                                    : currentIndex - 1
                                        }

                                        focusableElements[nextIndex]?.focus()
                                    }
                                }}
                            >
                                {showLoading && <LoadingSpinner />}

                                {/* level 1 */}
                                {root?.[itemsKey] &&
                                    !Object.keys(linksForContentComp)?.length > 0 && (
                                        <>
                                            <MainContentComponent
                                                item={root}
                                                itemsKey={itemsKey}
                                                // initialFocusRef={initialFocusRef}
                                                isMobileMenu={true}
                                                onClose={onClose}
                                                maxColumns={'2'}
                                                setfeatureImage={setfeatureImage}
                                                selectedLevelOneMenu={selectedLevelOneMenu}
                                                selectedLevelTwoMenu={selectedLevelTwoMenu}
                                                setSelectedLevelOneMenu={setSelectedLevelOneMenu}
                                                setLinksForLevelTwoMenu={setLinksForLevelTwoMenu}
                                                setIsLevelTwoMenuOpen={setIsLevelTwoMenuOpen}
                                                setIsLevelThreeMenuOpen={setIsLevelThreeMenuOpen}
                                                isLevelTwoMenuOpen={isLevelTwoMenuOpen}
                                                isLevelThreeMenuOpen={isLevelThreeMenuOpen}
                                                setLinksForContentComp={setLinksForContentComp}
                                                setFeatureImageMobileLevelTwo={
                                                    setFeatureImageMobileLevelTwo
                                                }
                                                setFeatureImageMobileLevelThree={
                                                    setFeatureImageMobileLevelThree
                                                }
                                                setFeatureImageMobileLevelFour={
                                                    setFeatureImageMobileLevelFour
                                                }
                                            />
                                        </>
                                    )}

                                {/* level 2 */}
                                {Object.keys(linksForContentComp)?.length > 0 &&
                                    !linksForLevelTwoMenu?.length > 0 && (
                                        <>
                                            {Object.keys(linksForContentComp)?.length > 0 && (
                                                <ListMenuContent
                                                    item={linksForContentComp}
                                                    itemsKey={itemsKey}
                                                    // initialFocusRef={initialFocusRef}
                                                    isMobileMenu={true}
                                                    setMenuToggle={setMenuToggle}
                                                    onClose={onClose}
                                                    maxColumns={'2'}
                                                    setfeatureImage={setfeatureImage}
                                                    selectedLevelOneMenu={selectedLevelOneMenu}
                                                    selectedLevelTwoMenu={selectedLevelTwoMenu}
                                                    setSelectedLevelOneMenu={
                                                        setSelectedLevelOneMenu
                                                    }
                                                    setSelectedLevelTwoMenu={
                                                        setSelectedLevelTwoMenu
                                                    }
                                                    setLinksForLevelThreeMenu={
                                                        setLinksForLevelThreeMenu
                                                    }
                                                    setLinksForLevelTwoMenu={
                                                        setLinksForLevelTwoMenu
                                                    }
                                                    setIsLevelTwoMenuOpen={setIsLevelTwoMenuOpen}
                                                    setIsLevelThreeMenuOpen={
                                                        setIsLevelThreeMenuOpen
                                                    }
                                                    isLevelTwoMenuOpen={isLevelTwoMenuOpen}
                                                    isLevelThreeMenuOpen={isLevelThreeMenuOpen}
                                                    setLinksForContentComp={setLinksForContentComp}
                                                    setFeatureImageMobileLevelTwo={
                                                        setFeatureImageMobileLevelTwo
                                                    }
                                                    setFeatureImageMobileLevelThree={
                                                        setFeatureImageMobileLevelThree
                                                    }
                                                    setFeatureImageMobileLevelFour={
                                                        setFeatureImageMobileLevelFour
                                                    }
                                                />
                                            )}
                                        </>
                                    )}

                                {/* level 3 */}
                                {linksForLevelTwoMenu?.length > 0 &&
                                    !linksForLevelThreeMenu?.length > 0 && (
                                        <>
                                            {linksForLevelTwoMenu?.length > 0 && (
                                                <ListMenuContentInner
                                                    items={linksForLevelTwoMenu}
                                                    onClose={onClose}
                                                    isMobileMenu={true}
                                                    setMenuToggle={setMenuToggle}
                                                    setfeatureImage={setfeatureImage}
                                                    selectedLevelOneMenu={selectedLevelOneMenu}
                                                    selectedLevelTwoMenu={selectedLevelTwoMenu}
                                                    setSelectedLevelTwoMenu={
                                                        setSelectedLevelTwoMenu
                                                    }
                                                    setLinksForLevelThreeMenu={
                                                        setLinksForLevelThreeMenu
                                                    }
                                                    setLinksForLevelTwoMenu={
                                                        setLinksForLevelTwoMenu
                                                    }
                                                    isLevelTwo={true}
                                                    setIsLevelTwoMenuOpen={setIsLevelTwoMenuOpen}
                                                    setIsLevelThreeMenuOpen={
                                                        setIsLevelThreeMenuOpen
                                                    }
                                                    isLevelTwoMenuOpen={isLevelTwoMenuOpen}
                                                    isLevelThreeMenuOpen={isLevelThreeMenuOpen}
                                                    setFeatureImageMobileLevelTwo={
                                                        setFeatureImageMobileLevelTwo
                                                    }
                                                    setFeatureImageMobileLevelThree={
                                                        setFeatureImageMobileLevelThree
                                                    }
                                                    setFeatureImageMobileLevelFour={
                                                        setFeatureImageMobileLevelFour
                                                    }
                                                />
                                            )}
                                        </>
                                    )}

                                {/* level 4 */}
                                {linksForLevelThreeMenu?.length > 0 && (
                                    <>
                                        {linksForLevelThreeMenu?.length > 0 && (
                                            <ListMenuContentInner
                                                items={linksForLevelThreeMenu}
                                                onClose={onClose}
                                                isMobileMenu={true}
                                                setMenuToggle={setMenuToggle}
                                                setfeatureImage={setfeatureImage}
                                                selectedLevelOneMenu={selectedLevelOneMenu}
                                                isLevelTwo={false}
                                                selectedLevelTwoMenu={selectedLevelTwoMenu}
                                                setSelectedLevelTwoMenu={setSelectedLevelTwoMenu}
                                                setLinksForLevelThreeMenu={
                                                    setLinksForLevelThreeMenu
                                                }
                                                setLinksForLevelTwoMenu={setLinksForLevelTwoMenu}
                                                setIsLevelTwoMenuOpen={setIsLevelTwoMenuOpen}
                                                setIsLevelThreeMenuOpen={setIsLevelThreeMenuOpen}
                                                isLevelTwoMenuOpen={isLevelTwoMenuOpen}
                                                isLevelThreeMenuOpen={isLevelThreeMenuOpen}
                                                setFeatureImageMobileLevelTwo={
                                                    setFeatureImageMobileLevelTwo
                                                }
                                                setFeatureImageMobileLevelThree={
                                                    setFeatureImageMobileLevelThree
                                                }
                                                setFeatureImageMobileLevelFour={
                                                    setFeatureImageMobileLevelFour
                                                }
                                            />
                                        )}
                                    </>
                                )}
                            </div>

                            {/* Global content slot (level 1) */}
                            {root?.[itemsKey] &&
                                !Object.keys(linksForContentComp)?.length > 0 &&
                                globalSlotLoading && (
                                    <Box
                                        marginTop={pxToRem(30)}
                                        display="flex"
                                        justifyContent="center"
                                    >
                                        <LoadingSpinner />
                                    </Box>
                                )}
                            {root?.[itemsKey] &&
                                !Object.keys(linksForContentComp)?.length > 0 &&
                                !globalSlotLoading &&
                                hasGlobalSlotContent && (
                                    <Box
                                        marginTop={pxToRem(30)}
                                        paddingLeft={pxToRem(11)}
                                        paddingRight={globalSlotContentCount > 1 ? 0 : pxToRem(11)}
                                        position="relative"
                                        overflow="visible"
                                    >
                                        <Box
                                            ref={globalContentSlotRef}
                                            sx={{
                                                ...mobileContentSlotStyles,
                                                ...(globalSlotContentCount === 1
                                                    ? {
                                                          '.shop-by-brand-carousel': {
                                                              ...mobileContentSlotStyles[
                                                                  '.shop-by-brand-carousel'
                                                              ],
                                                              li: {
                                                                  ...mobileContentSlotStyles[
                                                                      '.shop-by-brand-carousel'
                                                                  ].li,
                                                                  flex: '1 1 100%',
                                                                  width: '100%',
                                                                  '.shop-by-brand-image-wrapper-style1':
                                                                      {
                                                                          position: 'relative',
                                                                          width: '100%',
                                                                          height: 'auto',
                                                                          aspectRatio: '1 / 1',
                                                                          display: 'block',
                                                                          overflow: 'hidden'
                                                                      }
                                                              }
                                                          }
                                                      }
                                                    : {})
                                            }}
                                            minWidth={0}
                                            dangerouslySetInnerHTML={{
                                                __html: globalSlotResponse
                                            }}
                                        />
                                    </Box>
                                )}

                            {/* Login box (level 1) */}
                            {root?.[itemsKey] && !Object.keys(linksForContentComp)?.length > 0 && (
                                <Box paddingLeft={pxToRem(11)} paddingRight={pxToRem(11)}>
                                    <DrawerLoginBox
                                        isRegistered={isRegistered}
                                        onClose={handleClose}
                                        onSignout={onSignoutClick}
                                        onOpenAuthModal={(email) => {
                                            setDrawerEmail(email)
                                            authModal.onOpen()
                                        }}
                                        onInputBlur={handleDrawerInputBlur}
                                    />
                                </Box>
                            )}

                            {/* Category content slot (level 2) */}
                            {Object.keys(linksForContentComp)?.length > 0 &&
                                contentSlotId &&
                                contentSlotLoading && (
                                    <Box
                                        marginTop={pxToRem(30)}
                                        display="flex"
                                        justifyContent="center"
                                    >
                                        <LoadingSpinner />
                                    </Box>
                                )}
                            {Object.keys(linksForContentComp)?.length > 0 &&
                                !contentSlotLoading &&
                                hasSlotContent && (
                                    <Box
                                        marginTop={pxToRem(30)}
                                        paddingLeft={pxToRem(11)}
                                        paddingRight={slotContentCount > 1 ? 0 : pxToRem(11)}
                                    >
                                        <Box
                                            ref={mobileContentSlotRef}
                                            sx={{
                                                ...mobileContentSlotStyles,
                                                ...(slotContentCount === 1
                                                    ? {
                                                          '.shop-by-brand-carousel': {
                                                              ...mobileContentSlotStyles[
                                                                  '.shop-by-brand-carousel'
                                                              ],
                                                              li: {
                                                                  ...mobileContentSlotStyles[
                                                                      '.shop-by-brand-carousel'
                                                                  ].li,
                                                                  flex: '1 1 100%',
                                                                  width: '100%',
                                                                  '.shop-by-brand-image-wrapper-style1':
                                                                      {
                                                                          position: 'relative',
                                                                          width: '100%',
                                                                          height: 'auto',
                                                                          aspectRatio: '1 / 1',
                                                                          display: 'block',
                                                                          overflow: 'hidden'
                                                                      }
                                                              }
                                                          }
                                                      }
                                                    : {})
                                            }}
                                            minWidth={0}
                                            dangerouslySetInnerHTML={{
                                                __html: contentSlotResponse
                                            }}
                                        />
                                    </Box>
                                )}

                            {/* Login box */}
                            {Object.keys(linksForContentComp)?.length > 0 && (
                                <Box paddingLeft={pxToRem(11)} paddingRight={pxToRem(11)}>
                                    <DrawerLoginBox
                                        isRegistered={isRegistered}
                                        onClose={handleClose}
                                        onSignout={onSignoutClick}
                                        onOpenAuthModal={(email) => {
                                            setDrawerEmail(email)
                                            authModal.onOpen()
                                        }}
                                        onInputBlur={handleDrawerInputBlur}
                                    />
                                </Box>
                            )}

                            {/* action menu */}
                            {/* <Box {...styles.actionMenu}>
                                {isRegistered ? (
                                    <List {...styles.actionMenuItem}>
                                        <Link
                                            as={RouteLink}
                                            to="/account/info"
                                            {...styles.actionMenuItemLink}
                                            onClick={() => setMenuToggle((toggle) => !toggle)}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.account_settings',
                                                defaultMessage: 'Account Settings'
                                            })}
                                        </Link>
                                    </List>
                                ) : (
                                    <List {...styles.actionMenuItem}>
                                        <Link
                                            // href="/account/"
                                            {...styles.actionMenuItemLink}
                                            onClick={handleOnAccountClick}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.account_register',
                                                defaultMessage: 'Account/Register'
                                            })}
                                        </Link>
                                    </List>
                                )}
                                <List {...styles.actionMenuItem}>
                                    <Box>
                                        <Link
                                            onClick={handleTrackOrderClick}
                                            {...styles.actionMenuItemLink}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.track_my_order',
                                                defaultMessage: 'Track My Order'
                                            })}
                                        </Link>
                                        <Link
                                            href="/storelocator/"
                                            {...styles.actionMenuItemLink}
                                            onClick={() => setMenuToggle((toggle) => !toggle)}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.find_a_store',
                                                defaultMessage: 'Find a store'
                                            })}
                                        </Link>
                                    </Box>
                                </List>
                                <List {...styles.actionMenuItem}>
                                    <Box>
                                        <Link
                                            to="/help/"
                                            as={RouteLink}
                                            {...styles.actionMenuItemLink}
                                            onClick={() => setMenuToggle((toggle) => !toggle)}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.customer_service',
                                                defaultMessage: 'Customer Service'
                                            })}
                                        </Link>
                                        <Link
                                            to="/content/returns-exchanges.html"
                                            as={RouteLink}
                                            {...styles.actionMenuItemLink}
                                            onClick={() => setMenuToggle((toggle) => !toggle)}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.returns',
                                                defaultMessage: 'Returns'
                                            })}
                                        </Link>
                                    </Box>
                                </List>
                                <List {...styles.actionMenuItem}>
                                    <Box>
                                        <Link
                                            to="/sitemap"
                                            as={RouteLink}
                                            {...styles.actionMenuItemLink}
                                            onClick={() => setMenuToggle((toggle) => !toggle)}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.site_map',
                                                defaultMessage: 'Site Map'
                                            })}
                                        </Link>
                                        <Link
                                            to="/content/privacy-policy.html"
                                            as={RouteLink}
                                            {...styles.actionMenuItemLink}
                                            onClick={() => setMenuToggle((toggle) => !toggle)}
                                        >
                                            {intl.formatMessage({
                                                id: 'header.link.privacy_policy',
                                                defaultMessage: 'Privacy Policy'
                                            })}
                                        </Link>
                                        {isRegistered && (
                                            <Link
                                                {...styles.actionMenuItemLink}
                                                onClick={onSignoutClick}
                                                sx={{display: 'block'}}
                                            >
                                                {intl.formatMessage({
                                                    id: 'header.link.signout',
                                                    defaultMessage: 'Sign Out'
                                                })}
                                            </Link>
                                        )}
                                    </Box>
                                </List>
                            </Box> */}

                            {/* level 1 feature image */}
                            {/* {root?.[itemsKey] && !Object.keys(linksForContentComp)?.length > 0 && (
                                <Box
                                    {...styles.mobileFeatureImage}
                                    dangerouslySetInnerHTML={{__html: featureImage}}
                                ></Box>
                            )} */}

                            {/* level 2 feature image */}
                            {/* {Object.keys(linksForContentComp)?.length > 0 &&
                                !linksForLevelTwoMenu?.length > 0 && (
                                    <>
                                        {Object.keys(linksForContentComp)?.length > 0 && (
                                            <Box
                                                {...styles.mobileFeatureImage}
                                                dangerouslySetInnerHTML={{
                                                    __html: featureImageMobileLevelTwo
                                                }}
                                            ></Box>
                                        )}
                                    </>
                                )} */}

                            {/* level 3feature image  */}
                            {/* {linksForLevelTwoMenu?.length > 0 &&
                                !linksForLevelThreeMenu?.length > 0 && (
                                    <>
                                        {linksForLevelTwoMenu?.length > 0 && (
                                            <Box
                                                {...styles.mobileFeatureImage}
                                                dangerouslySetInnerHTML={{
                                                    __html: featureImageMobileLevelThree
                                                }}
                                            ></Box>
                                        )}
                                    </>
                                )} */}

                            {/* level 4 feature image */}
                            {/* {linksForLevelThreeMenu?.length > 0 && (
                                <>
                                    {linksForLevelThreeMenu?.length > 0 && (
                                        <Box
                                            {...styles.mobileFeatureImage}
                                            dangerouslySetInnerHTML={{
                                                __html: featureImageMobileLevelFour
                                            }}
                                        ></Box>
                                    )}
                                </>
                            )} */}
                        </DrawerBody>
                    </DrawerContent>
                </DrawerOverlay>
            </Drawer>
            <AuthModal {...authModal} initialEmail={drawerEmail} />
            <TrackOrderModal {...trackOrderModal} />
        </>
    )
}

DrawerMenu.displayName = 'DrawerMenu'

DrawerMenu.propTypes = {
    root: PropTypes.object,
    /**
     * The opened state of the drawer.
     */
    isOpen: PropTypes.bool,
    /**
     * Function called when the drawer is dismissed.
     */
    onClose: PropTypes.func,
    /**
     * Function called when the drawer logo is clicked.
     */
    onLogoClick: PropTypes.func,
    /**
     * Customize the property representing the items.
     */
    itemsKey: PropTypes.string,
    /**
     * Cusomtize the property representing the items count.
     */
    itemsCountKey: PropTypes.string,
    /**
     * Component to be rendered for each individual menu item.
     */
    isStickyActive: PropTypes.oneOf(true, false),
    promoBannerInHeaderIsActive: PropTypes.oneOf(true, false),
    setMenuToggle: PropTypes.func,
    menuToggle: PropTypes.bool,
    itemComponent: PropTypes.any
}

export {DrawerMenu}
